
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { DeliveryAddress } from "@/hooks/use-delivery-addresses";

interface DeliveryAddressFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (address: Omit<DeliveryAddress, 'id' | 'created_at' | 'updated_at'>) => void;
  initialData?: DeliveryAddress;
  organizationId: string;
  isLoading?: boolean;
}

export const DeliveryAddressForm = ({ 
  open, 
  onOpenChange, 
  onSubmit, 
  initialData,
  organizationId,
  isLoading = false
}: DeliveryAddressFormProps) => {
  const [formData, setFormData] = useState({
    contact_name: initialData?.contact_name || '',
    company_name: initialData?.company_name || '',
    address_line_1: initialData?.address_line_1 || '',
    address_line_2: initialData?.address_line_2 || '',
    city: initialData?.city || '',
    postal_code: initialData?.postal_code || '',
    country: initialData?.country || 'United Kingdom',
    phone_number: initialData?.phone_number || '',
    is_default: initialData?.is_default || false,
  });

  // Update form data when dialog opens with initial data
  useEffect(() => {
    if (open && initialData) {
      setFormData({
        contact_name: initialData.contact_name || '',
        company_name: initialData.company_name || '',
        address_line_1: initialData.address_line_1 || '',
        address_line_2: initialData.address_line_2 || '',
        city: initialData.city || '',
        postal_code: initialData.postal_code || '',
        country: initialData.country || 'United Kingdom',
        phone_number: initialData.phone_number || '',
        is_default: initialData.is_default || false,
      });
    } else if (open && !initialData) {
      // Reset form for new address
      setFormData({
        contact_name: '',
        company_name: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        postal_code: '',
        country: 'United Kingdom',
        phone_number: '',
        is_default: false,
      });
    }
  }, [open, initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      organization_id: organizationId,
    });
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {initialData ? 'Edit Delivery Address' : 'Add New Delivery Address'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="contact_name">Contact Name *</Label>
            <Input
              id="contact_name"
              value={formData.contact_name}
              onChange={(e) => handleInputChange('contact_name', e.target.value)}
              required
            />
          </div>

          <div>
            <Label htmlFor="company_name">Company Name</Label>
            <Input
              id="company_name"
              value={formData.company_name}
              onChange={(e) => handleInputChange('company_name', e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="address_line_1">Address Line 1 *</Label>
            <Input
              id="address_line_1"
              value={formData.address_line_1}
              onChange={(e) => handleInputChange('address_line_1', e.target.value)}
              required
            />
          </div>

          <div>
            <Label htmlFor="address_line_2">Address Line 2</Label>
            <Input
              id="address_line_2"
              value={formData.address_line_2}
              onChange={(e) => handleInputChange('address_line_2', e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="city">City *</Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="postal_code">Postal Code *</Label>
              <Input
                id="postal_code"
                value={formData.postal_code}
                onChange={(e) => handleInputChange('postal_code', e.target.value)}
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="country">Country *</Label>
            <Input
              id="country"
              value={formData.country}
              onChange={(e) => handleInputChange('country', e.target.value)}
              required
            />
          </div>

          <div>
            <Label htmlFor="phone_number">Phone Number *</Label>
            <Input
              id="phone_number"
              type="tel"
              value={formData.phone_number}
              onChange={(e) => handleInputChange('phone_number', e.target.value)}
              required
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="is_default"
              checked={formData.is_default}
              onCheckedChange={(checked) => handleInputChange('is_default', checked as boolean)}
            />
            <Label htmlFor="is_default">Set as default address</Label>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isLoading}
              style={{ 
                backgroundColor: '#DFEF97', 
                color: 'black',
                border: '1px solid #DFEF97'
              }}
              className="hover:opacity-90 disabled:opacity-50"
            >
              {isLoading ? 'Saving...' : (initialData ? 'Update' : 'Create')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
